<template>
  <v-form>
    <!-- Blocks parent forms from registering internal v-text-field -->
    <v-text-field
      ref="textField"
      :value="blur ? blurValue : durationValue"
      @input="durationValueChange"
      :rules="durationRules"
      :suffix="displayOnly ? null : durationType"
      :class="`duration-edit number-without-spinners ${blur ? 'blur' : ''}`"
      @click.stop
      @mousedown.stop
      @click:clear="clear"
      :append-icon="appendIcon"
      :append-outer-icon="appendOuterIcon"
      :background-color="backgroundColor"
      :clearable="clearable"
      :dense="dense"
      :disabled="disabled"
      :error="error"
      :error-messages="errorMessages"
      :hide-details="hideDetails"
      :hint="hint"
      :label="label"
      :persistent-hint="persistentHint"
      :prepend-icon="prependIcon"
      :prepend-inner-icon="prependInnerIcon"
      :readonly="readonly || blur"
      @keypress="keypress"
    >
      <template v-slot:append v-if="!displayOnly">
        <v-menu v-model="durationTypeMenu" mandatory left>
          <template v-slot:activator="{ on }">
            <v-icon class="duration-type-arrow" v-on="on">mdi-menu-down</v-icon>
          </template>
          <v-list class="duration-type-list">
            <v-list-item-group :value="durationType" @change="durationTypeChange" color="primary" mandatory>
              <v-list-item v-for="(item, index) in durationTypes" :key="index" :value="item">
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
        <slot name="append"></slot>
      </template>
    </v-text-field>
  </v-form>
</template>
<script>
import { Convert } from './convert'
import { Rules } from './rules'
import { Duration } from 'luxon'
import { Time } from './time'

const supportedUnits = ['seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years']
const zeroDurations = {
  seconds: 'PT0S',
  minutes: 'PT0M',
  hours: 'PT0H',
  days: 'P0D',
  weeks: 'P0W',
  months: 'P0M',
  years: 'P0Y'
}

export default {
  name: 'DurationEdit',
  props: {
    value: {
      type: String,
      description: 'Duration iso value'
    },
    durationTypes: {
      type: Array,
      description: 'Units of time allowed',
      default() {
        return ['hours', 'days', 'weeks', 'months', 'years']
      },
      validator(value) {
        return value.every(x => supportedUnits.includes(x))
      }
    },
    appendIcon: String,
    appendOuterIcon: String,
    backgroundColor: String,
    clearable: Boolean,
    dense: Boolean,
    disabled: Boolean,
    error: Boolean,
    errorMessages: [String, Array],
    hideDetails: {
      type: [Boolean, String],
      default: 'auto'
    },
    hint: String,
    label: String,
    persistentHint: Boolean,
    prependIcon: String,
    prependInnerIcon: String,
    readonly: Boolean,
    rules: Array,
    blur: Boolean,
    defaultDurationType: {
      type: String,
      default() {
        return 'days'
      }
    },
    suffix: {
      type: String,
      default: null
    }
  },
  inject: {
    form: { default: null }
  },
  data() {
    return {
      durationTypeMenu: false,
      specifiedDurationValue: null,
      selectedDurationType: null,
      humanizedDurationValue: null
    }
  },
  watch: {
    value() {
      this.decomposeValue()
    }
  },
  computed: {
    blurValue() {
      return !this.durationValue ? '#' : this.durationValue.toString().replace(/\d/g, '#')
    },
    displayOnly() {
      return this.disabled || this.readonly || this.blur
    },
    durationType() {
      return this.selectedDurationType
    },
    durationValue() {
      return this.displayOnly ? this.humanizedDurationValue : this.specifiedDurationValue
    },
    durationRules() {
      return this.displayOnly ? [] : this.rules ? [...this.rules, Rules.isInteger] : [Rules.isInteger]
    },
    errorBucket() {
      return this.textField ? this.textField.errorBucket : []
    }
  },
  methods: {
    durationValueChange(v) {
      let finalValue
      v = !v ? v : v.replace(/,/g, '').trim()
      if (!v) {
        finalValue = null
      } else {
        let number = Convert.toInteger(v.replace(/,/g, ''), null)
        finalValue = !isNaN(number) ? number : v
      }

      this.specifiedDurationValue = finalValue
      if (!this.selectedDurationType) {
        this.selectedDurationType = this.defaultDurationType
      }
      this.durationChanged()
    },
    durationTypeChange(v) {
      this.selectedDurationType = v !== undefined ? v : this.defaultDurationType
      this.durationChanged()
    },
    durationChanged() {
      if (this.displayOnly || !Rules.isInteger(this.durationValue) === true) {
        return
      }

      if (this.durationValue === 0 && this.durationType) {
        this.$emit('input', zeroDurations[this.durationType])
      } else if ((this.durationValue || this.durationValue === 0) && this.durationType) {
        const json = JSON.parse('{"' + this.durationType + '": ' + this.durationValue + '}')
        const duration = Duration.fromObject(json)
        const iso = duration.toISO()
        const checkDuration = Duration.fromISO(iso)
        const result = checkDuration.isValid ? iso : null
        this.$emit('input', result)
      } else if (!this.durationValue) {
        this.$emit('input', null)
      }
    },
    getUnit(val, durationType) {
      return !val || val <= 1 ? durationType.substring(0, durationType.length - 1) : durationType
    },
    decomposeValue() {
      if (!this.value) {
        this.specifiedDurationValue = null
        this.humanizedDurationValue = null
        if (!this.selectedDurationType) {
          this.selectedDurationType = this.defaultDurationType
        }
        return
      }

      let duration = Duration.fromISO(this.value)
      if (!duration || (duration && !duration.values)) {
        this.specifiedDurationValue = null
        if (!this.selectedDurationType) {
          this.selectedDurationType = this.defaultDurationType
        }
        this.humanizedDurationValue = null
        return
      }

      this.humanizedDurationValue = Time.safeToHuman(this.value)
      if (this.suffix) {
        this.humanizedDurationValue += ` ${this.suffix}`
      }

      for (const type of this.durationTypes) {
        if (Object.prototype.hasOwnProperty.call(duration.values, type)) {
          this.selectedDurationType = type
          this.specifiedDurationValue = duration.as(type)
          return
        }
      }

      this.specifiedDurationValue = null
      this.selectedDurationType = this.defaultDurationType
    },
    clear() {
      if (this.displayOnly) return

      this.$emit('click:clear')
    },
    focus() {
      this.$refs.textField.focus()
    },
    keypress(evt) {
      //  Filter to number characters
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode >= 48 && charCode <= 57) {
        return true
      }
      evt.preventDefault()
    },
    validate() {
      const valid = this.$refs.textField.validate()
      if (!valid) {
        this.$refs.textField.hasFocused = true
      }
      return valid
    },
    reset() {
      return this.$refs.textField.reset()
    },
    resetValidation() {
      return this.$refs.textField.resetValidation()
    }
  },
  created() {
    this.form?.register(this)
    this.decomposeValue()
  },
  beforeDestroy() {
    this.form?.unregister(this)
  }
}
</script>
<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';
.duration-type-list {
  min-width: 120px;
}
.duration-type-arrow::before {
  background-color: transparent !important;
}
.v-application .error--text .theme--light.duration-type-arrow {
  color: var(--v-error-base);
}
.v-application .primary--text .theme--light.duration-type-arrow {
  color: var(--v-primary-base);
}
.v-input.duration-edit.number-without-spinners.blur::v-deep input[type='text'] {
  color: transparent;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
</style>
